import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import {
  addLocalAccessKeyAsync,
  deleteLocalAccessKeysAsync,
  getLocalAccessKeysAsync,
} from '../../api/keyApi'
import { GenericKeyResponseDto, KeysResponseDto } from '../../api/kmsapiclient'
import { KeyInfo, SearchKeyInfo } from '../../components/common/types'
import {
  DELETE_KEYS_PERMISSION,
  GET_KEYS_PERMISSION,
  UPLOAD_KEYS_PERMISSION,
} from '../../redux/actions/actionTypes'
import {
  apiCallError,
  beginApiCall,
  endApiCall,
} from '../../redux/actions/apiStatusActions'
import {
  addKeySuccess,
  deleteKeySuccess,
  loadKeysSuccess,
  loadMoreKeysSuccess,
} from '../../redux/actions/keyActions'
import { store, useAppDispatch } from '../../redux/configureStore'
import { IRootState } from '../../redux/reducers'
import { KeysReducerState } from '../../redux/reducers/initialState'
import { GetPageNum } from '../../utils'
import KeyList from '../common/KeyList'
import { usePermissions } from '../common/permission/usePermissions'
import { validateKey } from '../common/utils'

export const ManageKeysPage = () => {
  const keys: KeysReducerState = useSelector((state: IRootState) => state.keys)
  const loading: boolean = useSelector(
    (state: IRootState) => state.apiCallsInProgress > 0,
  )

  const { checkPermissions } = usePermissions()
  const defaultSearchKeyRequest: SearchKeyInfo = {
    keyId: '',
    keyName: '',
    aadRevocation: '',
  }
  const [keysRequest, setKeysRequest] = useState<SearchKeyInfo>(
    defaultSearchKeyRequest,
  )
  const disableLoadMore = !(keysRequest.continuationToken === null)
  const [pageNum, setPageNum] = useState(1)
  const [keysExists, setKeysExists] = useState(true)
  const firstUpdate = useRef(true)
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false
      return
    }
    setKeysExists(keys.keys.length > 0)

    if (validateKey(keys, keysRequest)) {
      setKeysRequest({
        ...keysRequest,
        continuationToken: keys.continuationToken,
      })
    }
  }, [keysRequest, keys, keys.keys])

  const handleDeleteKey = async (key: KeyInfo) => {
    checkPermissions(store.getState(), DELETE_KEYS_PERMISSION)

    dispatch(beginApiCall())
    try {
      const deletedKey: string = await deleteLocalAccessKeysAsync(key)
      if (deletedKey === 'Success') {
        dispatch(deleteKeySuccess(key))
        toast.success(t('key.key_deleted'))
      } else {
        dispatch(endApiCall())
        toast.error(t('key.key_delete_failed'))
      }
    } catch (error) {
      dispatch(apiCallError())
      throw error
    }
  }

  const handleSearch = () => {
    searchKeys(keysRequest, loadKeysSuccess)
  }

  const handleLoadMore = () => {
    searchKeys(keysRequest, loadMoreKeysSuccess)
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchCriteria(event.target.name, event.target.value)
  }

  const handleBlur = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchCriteria(event.target.name, event.target.value.trim())
  }

  const setSearchCriteria = (name, value) => {
    const newGetKeysRequest = {
      ...keysRequest,
      [name]: value,
    }
    setKeysRequest(newGetKeysRequest)
  }

  const searchKeys = async (request, successFunc) => {
    checkPermissions(store.getState(), GET_KEYS_PERMISSION)

    dispatch(beginApiCall())
    try {
      const keys: KeysResponseDto = await getLocalAccessKeysAsync(request)

      dispatch(successFunc(keys))

      const length = [keys].length
      setPageNum(GetPageNum(length))
    } catch (error) {
      dispatch(apiCallError())
      throw error
    }
  }

  const addKey = async (file, keyName, aadRevocation) => {
    checkPermissions(store.getState(), UPLOAD_KEYS_PERMISSION)
    dispatch(beginApiCall())
    try {
      const result: GenericKeyResponseDto = await addLocalAccessKeyAsync(
        file,
        keyName,
        aadRevocation,
      )
      if (result != null) {
        dispatch(addKeySuccess(result))
        toast.success(t('key.local_access_keys_added'))
        setPageNum(1)
      } else {
        dispatch(endApiCall())
        toast.error(t('key.local_access_keys_add_failed'))
      }
    } catch (error) {
      toast.error(t('key.local_access_keys_add_failed'))
      dispatch(apiCallError())
      throw error
    }
  }

  return (
    <div className="container">
      <h3 className="page-header">{t('manage.local_access_keys')}</h3>
      <KeyList
        keys={keys.keys}
        loading={loading}
        onSearchClick={handleSearch}
        onDeleteClick={handleDeleteKey}
        onLoadMoreClick={handleLoadMore}
        handleChange={handleChange}
        handleBlur={handleBlur}
        keysExists={keysExists}
        request={keysRequest}
        pageNum={pageNum}
        disableLoadMore={disableLoadMore}
        updatePageNum={setPageNum}
        editKeyPath="/keys/"
        showUploadButton={true}
        uploadKey={addKey}
      />
    </div>
  )
}
